import { getEffectiveFirebaseIdToken } from '@/features/impersonation/effectiveFirebaseToken'

export type VoiceChannel = 'meta' | 'web'
export type VoiceDirection = 'INBOUND' | 'OUTBOUND'

export type OperatorCall = {
  id: string
  runId?: string
  providerCallId?: string
  agentId?: string
  channel: string
  state?: string
  phone: string
  profileName?: string
  profilePicUrl?: string
  direction?: VoiceDirection
  contactContext?: Record<string, unknown>
  outcome?: string
  startedAt?: string
  claimAction?: 'accept' | 'reject'
  claimedBy?: { id?: string; name?: string; email?: string }
  acceptedBy?: { id?: string; name?: string; email?: string }
}

export type VoiceSession = {
  runId: string
  providerCallId?: string
  accessToken: string
  livekitUrl: string
  participantIdentity?: string
}

export type TelephonyConfig = {
  id: string
  agentId?: string
  name: string
  provider: string
  active: boolean
  phoneNumberId?: string
  wabaId?: string
  outboundAddress?: string
  inboundEnabled?: boolean
  inboundMode?: 'MANUAL' | 'AI'
  ringtone?: 'CALL_1' | 'CALL_2' | 'CALL_3' | 'OFF'
  inboundWorkflowId?: string
  trunkId?: string
  inboundTrunkId?: string
  dispatchRuleId?: string
  maxDurationSeconds?: number
  revision?: number
}

export type MetaCallPermission = {
  status: string
  expirationTime?: number
  canStartCall: boolean
  canRequestPermission: boolean
}

export type MetaProvisioningStatus = {
  state: 'ELIGIBLE' | 'INCOMPLETE' | 'CONFLICT' | 'META_READY' | 'READY' | 'UNSUPPORTED'
  coexistence?: boolean
  platformType?: string
  recommendedAction: 'NONE' | 'ADOPT' | 'ADD' | 'REPLACE'
  desired?: { hostname?: string; port?: number }
  meta?: {
    callingEnabled?: boolean
    sipEnabled?: boolean
    matchingServer?: boolean
    g711Enabled?: boolean
    conflict?: boolean
  }
  livekit?: {
    outboundTrunkReady?: boolean
    inboundTrunkReady?: boolean
    dispatchReady?: boolean
    webhookReady?: boolean
  }
}

export type VoiceCallHistoryItem = {
  id: string
  agentId?: string
  channel?: string
  direction: VoiceDirection
  phone: string
  state?: string
  outcome?: string
  startedAt: string
  endedAt?: string
  connectedAt?: string
  wasConnected: boolean
  durationSeconds: number
  connectionOperationId?: string
  claimedBy?: { id?: string; name?: string; email?: string }
  acceptedBy?: { id?: string; name?: string; email?: string }
}

async function authHeaders(companyId: string, json = true) {
  const token = await getEffectiveFirebaseIdToken()
  return {
    Authorization: `Bearer ${token}`,
    'X-Company-Id': companyId,
    ...(json ? { 'Content-Type': 'application/json' } : {})
  }
}

async function request<T>(companyId: string, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // A autenticação da voz é explícita pelo Bearer token. Não envie o cookie
    // de sessão (que contém o perfil do usuário e pode exceder o limite HTTP).
    credentials: 'omit',
    headers: { ...(await authHeaders(companyId)), ...(init.headers || {}) }
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(body.message || `Falha na ligação (${response.status})`)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

const OPERATOR_BASE = '/api/voice/v1/operator'

export const officialVoiceApi = {
  async listConfigs(companyId: string) {
    return request<{ items: TelephonyConfig[] }>(companyId, `${OPERATOR_BASE}/telephony-configs`)
  },
  async metaEligibility(companyId: string, phoneNumberId: string) {
    return request<MetaProvisioningStatus>(
      companyId,
      `${OPERATOR_BASE}/meta-eligibility?phoneNumberId=${encodeURIComponent(phoneNumberId)}`
    )
  },
  async operatorProvisioningStatus(companyId: string, configId: string) {
    return request<MetaProvisioningStatus>(
      companyId,
      `${OPERATOR_BASE}/telephony-configs/${encodeURIComponent(configId)}/provisioning`
    )
  },
  async listCallSettings(companyId: string) {
    return request<{ items: TelephonyConfig[] }>(companyId, `${OPERATOR_BASE}/call-settings/telephony-configs`)
  },
  async callHistory(companyId: string, agentId: string, phone: string, signal?: AbortSignal) {
    const query = new URLSearchParams({ agentId, phone, limit: '100' })
    return request<{ items: VoiceCallHistoryItem[] }>(companyId, `${OPERATOR_BASE}/call-history?${query}`, { signal })
  },
  async saveCallSettings(companyId: string, config: Partial<TelephonyConfig>) {
    const id = config.id ? `/${encodeURIComponent(config.id)}` : ''
    return request<TelephonyConfig>(companyId, `${OPERATOR_BASE}/call-settings/telephony-configs${id}`, {
      method: config.id ? 'PUT' : 'POST',
      body: JSON.stringify(config)
    })
  },
  async provisioningStatus(companyId: string, configId: string) {
    return request<MetaProvisioningStatus>(
      companyId,
      `${OPERATOR_BASE}/call-settings/telephony-configs/${encodeURIComponent(configId)}/provisioning`
    )
  },
  async provision(companyId: string, configId: string, strategy: 'ADOPT' | 'ADD' | 'REPLACE', confirmReplace = false) {
    return request<{ status: MetaProvisioningStatus; config: TelephonyConfig }>(
      companyId,
      `${OPERATOR_BASE}/call-settings/telephony-configs/${encodeURIComponent(configId)}/provisioning`,
      { method: 'POST', body: JSON.stringify({ strategy, confirmReplace }) }
    )
  },
  async callPermission(companyId: string, configId: string, phone: string) {
    return request<MetaCallPermission>(
      companyId,
      `${OPERATOR_BASE}/telephony-configs/${encodeURIComponent(configId)}/call-permission?to=${encodeURIComponent(
        phone
      )}`
    )
  },
  async requestCallPermission(companyId: string, configId: string, phone: string, message: string) {
    return request<{ requestSent: boolean; permission: MetaCallPermission }>(
      companyId,
      `${OPERATOR_BASE}/telephony-configs/${encodeURIComponent(configId)}/call-permission/request`,
      { method: 'POST', body: JSON.stringify({ to: phone, message }) }
    )
  },
  async start(companyId: string, telephonyConfigId: string, phone: string, initialContext: Record<string, unknown>) {
    return request<VoiceSession>(companyId, `${OPERATOR_BASE}/calls`, {
      method: 'POST',
      body: JSON.stringify({ telephonyConfigId, to: phone, maxDurationSeconds: 14_400, initialContext })
    })
  },
  async accept(companyId: string, runId: string) {
    return request<VoiceSession>(companyId, `${OPERATOR_BASE}/incoming-calls/${encodeURIComponent(runId)}/accept`, {
      method: 'POST',
      body: '{}'
    })
  },
  async reject(companyId: string, runId: string) {
    return request<void>(companyId, `${OPERATOR_BASE}/incoming-calls/${encodeURIComponent(runId)}/reject`, {
      method: 'POST',
      body: '{}'
    })
  },
  async claim(companyId: string, runId: string, action: 'accept' | 'reject') {
    return request<OperatorCall>(companyId, `${OPERATOR_BASE}/incoming-calls/${encodeURIComponent(runId)}/claim`, {
      method: 'POST',
      body: JSON.stringify({ action })
    })
  },
  async terminate(
    companyId: string,
    runId: string,
    reason?:
      | 'OPERATOR_HANGUP'
      | 'OPERATOR_CANCELLED'
      | 'REMOTE_HANGUP'
      | 'REMOTE_REJECTED'
      | 'RETRY_SUPERSEDED'
      | 'NO_ANSWER',
    connectedAt?: number
  ) {
    return request<void>(companyId, `${OPERATOR_BASE}/calls/${encodeURIComponent(runId)}/terminate`, {
      method: 'POST',
      body: JSON.stringify({
        ...(reason ? { reason } : {}),
        ...(connectedAt ? { connectedAt: new Date(connectedAt).toISOString() } : {})
      })
    })
  },
  async markActive(companyId: string, runId: string) {
    return request<OperatorCall>(companyId, `${OPERATOR_BASE}/calls/${encodeURIComponent(runId)}/active`, {
      method: 'POST',
      body: '{}'
    })
  }
}

export async function subscribeOperatorCalls(
  companyId: string,
  signal: AbortSignal,
  onEvent: (type: string, payload: { call?: OperatorCall; items?: OperatorCall[] }) => void
) {
  const response = await fetch(`${OPERATOR_BASE}/events`, {
    signal,
    credentials: 'omit',
    headers: await authHeaders(companyId, false)
  })
  if (!response.ok || !response.body) throw new Error('Canal de ligações indisponível')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (!signal.aborted) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let boundary = buffer.indexOf('\n\n')
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      const event = block.match(/^event:\s*(.+)$/m)?.[1]
      const data = block.match(/^data:\s*(.+)$/m)?.[1]
      if (event && data) {
        try {
          onEvent(event, JSON.parse(data))
        } catch {
          // Ignore a malformed event; the next snapshot reconciles state.
        }
      }
      boundary = buffer.indexOf('\n\n')
    }
  }
}

export const webVoiceApi = {
  async register(companyId: string, campaignId: string) {
    return request<{ id: string; campaignId: string; instance: string }>(
      companyId,
      `/api/web-calls/campaigns/${encodeURIComponent(campaignId)}/register`,
      { method: 'POST', body: '{}' }
    )
  },
  async start(companyId: string, campaignId: string, phone: string) {
    return request<{ results: { callId: string } }>(
      companyId,
      `/api/web-calls/campaigns/${encodeURIComponent(campaignId)}/start`,
      { method: 'POST', body: JSON.stringify({ phone, video: false }) }
    )
  },
  async action(companyId: string, campaignId: string, action: 'accept' | 'reject' | 'terminate', callId: string) {
    return request<unknown>(companyId, `/api/web-calls/campaigns/${encodeURIComponent(campaignId)}/${action}`, {
      method: 'POST',
      body: JSON.stringify({ callId })
    })
  },
  async ticket(companyId: string, campaignId: string, callId: string) {
    return request<{ results: { streamPath: string } }>(
      companyId,
      `/api/web-calls/campaigns/${encodeURIComponent(campaignId)}/calls/${encodeURIComponent(callId)}/media-ticket`,
      { method: 'POST', body: '{}' }
    )
  }
}
